import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpRight,
  BarChart3,
  Bot,
  CheckCircle2,
  GitBranch,
  Rocket,
  Sparkles,
  User,
  Wand2,
  Pause,
  Play,
  RotateCcw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ChatBar } from "../../features/kquant/components/ChatBar";
import { FlowCanvas } from "../StrategiesDemoPage/components/flow/FlowCanvas";
import VideoCarousel from './VideoCarousel';
import type { VideoSlide } from './VideoCarousel';
const USER_PROMPT = "Help me create a simple quant trading strategy.";
const AI_REPLY =
  "Sure — answer a few quick preferences, and I'll generate a complete strategy flow and backtest it for you.";

const STRATEGIES_SLIDES: VideoSlide[] = [
    {
        src: '/assets/home/quant4.mp4',
        label: 'Historical Price Preparation',
        caption: 'Downloading historical price data.',
    },
    {
        src: '/assets/home/quant5.mp4',
        label: 'Quantitative Strategy Development',
        caption: 'Developing quantitative trading strategies.',
    },
    {
        src: '/assets/home/quant6.mp4',
        label: 'Backtesting and Strategy Validation',
        caption: 'Validating the effectiveness of the strategies by writing backtest code.',
    },
];

type Panel = "prefs" | "flow" | "backtest";

function buildSparkPath(points: Array<[number, number]>) {
  if (points.length === 0) return "";
  const [x0, y0] = points[0];
  let d = `M ${x0} ${y0}`;
  for (let i = 1; i < points.length; i += 1) {
    const [x, y] = points[i];
    d += ` L ${x} ${y}`;
  }
  return d;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n));
}

// Step durations: [chat 3000ms, flow 5000ms, backtest 1500ms]
const STEP_DURATIONS = [3000, 5000, 1500];
const STEP_LABELS = ["Chat", "Build Flow", "Backtest"];

function StepProgressBar({
  currentStep,
  stepProgress,
  isPaused,
  onStepClick,
  onPause,
  onReplay,
}: {
  currentStep: number;
  stepProgress: number;
  isPaused: boolean;
  onStepClick: (step: number) => void;
  onPause: () => void;
  onReplay: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-2.5">
        {STEP_LABELS.map((label, i) => {
          const isCompleted = i < currentStep;
          const isActive = i === currentStep;
          return (
            <button
              key={label}
              onClick={() => onStepClick(i)}
              title={label}
              className="relative flex items-center cursor-pointer"
              style={{ outline: "none" }}
            >
              {isActive ? (
                <div
                  className="relative overflow-hidden rounded-full bg-slate-200"
                  style={{ width: 48, height: 8 }}
                >
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-blue-500"
                    style={{
                      width: `${Math.round(stepProgress * 100)}%`,
                      transition: "width 80ms linear",
                    }}
                  />
                </div>
              ) : (
                <div
                  className={`rounded-full transition-all duration-300 ${
                    isCompleted
                      ? "bg-blue-400 border border-blue-400"
                      : "bg-slate-200 border border-slate-300"
                  }`}
                  style={{ width: 8, height: 8 }}
                />
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onPause}
          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          title={isPaused ? "Resume" : "Pause"}
        >
          {isPaused ? <Play size={14} /> : <Pause size={14} />}
        </button>
        <button
          onClick={onReplay}
          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          title="Replay"
        >
          <RotateCcw size={14} />
        </button>
      </div>
    </div>
  );
}

export default function StrategiesIntroSection() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const [stage, setStage] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [hasStarted, setHasStarted] = useState(false);
  const [panel, setPanel] = useState<Panel>("prefs");
  const [flowStep, setFlowStep] = useState(0);
  const [backtestState, setBacktestState] = useState<"idle" | "running" | "done">("idle");

  const [isPaused, setIsPaused] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepProgress, setStepProgress] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const isPausedRef = useRef(false);
  const pausedAtRef = useRef<number>(0);
  const rafRef = useRef<number | undefined>(undefined);
  const timeoutsRef = useRef<number[]>([]);
  const typingTimerRef = useRef<number | undefined>(undefined);
  const startedRef = useRef(false);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    return Boolean(mq?.matches);
  }, []);

  const backtestPath = useMemo(() => {
    const pts: Array<[number, number]> = [];
    const w = 520;
    const h = 180;
    for (let i = 0; i <= 24; i += 1) {
      const t = i / 24;
      const x = 24 + t * (w - 48);
      const base = 0.55 + 0.35 * t;
      const dip = Math.exp(-Math.pow((t - 0.68) / 0.09, 2)) * 0.18;
      const noise = Math.sin(t * 8.2) * 0.015;
      const v = clamp(base - dip + noise, 0.05, 0.95);
      const y = 18 + (1 - v) * (h - 36);
      pts.push([x, y]);
    }
    return buildSparkPath(pts);
  }, []);

  const clearAll = useCallback(() => {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id));
    timeoutsRef.current = [];
    if (typingTimerRef.current !== undefined) {
      window.clearInterval(typingTimerRef.current);
      typingTimerRef.current = undefined;
    }
    if (rafRef.current !== undefined) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }
  }, []);

  const runStepProgress = useCallback((step: number, startProgress: number = 0) => {
    const duration = STEP_DURATIONS[step] ?? 2000;
    const startTime = performance.now() - startProgress * duration;

    const tick = (now: number) => {
      if (isPausedRef.current) return;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setStepProgress(progress);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const startAnimation = useCallback(
    (fromStep: number = 0) => {
      clearAll();
      isPausedRef.current = false;
      pausedAtRef.current = 0;
      setIsPaused(false);

      if (prefersReducedMotion) {
        setStage(8);
        setTypedText(AI_REPLY);
        setPanel("backtest");
        setFlowStep(4);
        setBacktestState("done");
        setCurrentStep(2);
        setStepProgress(1);
        return;
      }

      // Jump to starting visual state for the chosen step
      if (fromStep === 0) {
        setStage(0);
        setTypedText("");
        setPanel("prefs");
        setFlowStep(0);
        setBacktestState("idle");
      } else if (fromStep === 1) {
        // Start of "Build Flow" step: prefs done, typing done
        setStage(4);
        setTypedText(AI_REPLY);
        setPanel("flow");
        setFlowStep(1);
        setBacktestState("idle");
      } else if (fromStep === 2) {
        // Start of "Backtest" step: flow done
        setStage(6);
        setTypedText(AI_REPLY);
        setPanel("backtest");
        setFlowStep(4);
        setBacktestState("running");
      }

      setCurrentStep(fromStep);
      setStepProgress(0);
      runStepProgress(fromStep);

      const schedule = (ms: number, fn: () => void) => {
        const id = window.setTimeout(() => {
          if (isPausedRef.current) return;
          fn();
        }, ms);
        timeoutsRef.current.push(id);
      };

      // Step 0: Chat phase (0 → 3000ms)
      if (fromStep <= 0) {
        schedule(500, () => setStage(1));
        schedule(1000, () => setStage(2));
        schedule(1500, () => {
          let i = 0;
          typingTimerRef.current = window.setInterval(() => {
            if (isPausedRef.current) return;
            i = Math.min(i + 2, AI_REPLY.length);
            setTypedText(AI_REPLY.slice(0, i));
            if (i >= AI_REPLY.length && typingTimerRef.current !== undefined) {
              window.clearInterval(typingTimerRef.current);
              typingTimerRef.current = undefined;
            }
          }, 12);
        });
        schedule(2000, () => setStage(3));

        // Step 0 → 1 transition at 3000ms
        schedule(3000, () => {
          setStage(4);
          setPanel("flow");
          setFlowStep(1);
          setCurrentStep(1);
          setStepProgress(0);
          runStepProgress(1);
        });
      }

      // Step 1: Build Flow phase (0 or 3000ms offset)
      const flowOffset = fromStep <= 0 ? 3000 : 0;
      if (fromStep <= 1) {
        schedule(flowOffset + 1000, () => setFlowStep(2));
        schedule(flowOffset + 2000, () => setFlowStep(3));
        schedule(flowOffset + 3000, () => setFlowStep(4));

        // Step 1 → 2 transition at flowOffset + 5000ms
        schedule(flowOffset + 5000, () => {
          setStage(6);
          setPanel("backtest");
          setBacktestState("running");
          setCurrentStep(2);
          setStepProgress(0);
          runStepProgress(2);
        });
      }

      // Step 2: Backtest phase
      const backtestOffset = fromStep <= 0 ? 8000 : fromStep === 1 ? 5000 : 0;
      if (fromStep <= 2) {
        schedule(backtestOffset + 1000, () => {
          setStage(8);
          setBacktestState("done");
          setStepProgress(1);
        });
      }
    },
    [clearAll, prefersReducedMotion, runStepProgress]
  );

  // IntersectionObserver trigger
  useEffect(() => {
    if (hasStarted) return;
    const el = containerRef.current;
    if (!el) return;

    if (prefersReducedMotion) {
      setHasStarted(true);
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setHasStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.intersectionRatio >= 0.3) {
          setHasStarted(true);
          observer.disconnect();
        }
      },
      { threshold: [0, 0.3, 1] }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasStarted, prefersReducedMotion]);

  useEffect(() => {
    if (!hasStarted) return;
    if (startedRef.current) return;
    startedRef.current = true;
    startAnimation(0);
    return () => clearAll();
  }, [hasStarted, startAnimation, clearAll]);

  const handlePause = useCallback(() => {
    if (isPausedRef.current) {
      isPausedRef.current = false;
      setIsPaused(false);
      runStepProgress(currentStep, pausedAtRef.current);
    } else {
      isPausedRef.current = true;
      setIsPaused(true);
      pausedAtRef.current = stepProgress;
      if (rafRef.current !== undefined) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = undefined;
      }
    }
  }, [currentStep, stepProgress, runStepProgress]);

  const handleReplay = useCallback(() => {
    startedRef.current = true;
    startAnimation(0);
  }, [startAnimation]);

  const handleStepClick = useCallback(
    (step: number) => {
      startedRef.current = true;
      startAnimation(step);
    },
    [startAnimation]
  );

  const prefsStep = stage <= 2 ? 0 : stage === 3 ? 3 : stage >= 4 ? 5 : 0;

  return (
    <div
      ref={containerRef}
      className="relative h-screen overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white text-slate-900"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 right-[-120px] h-[520px] w-[520px] rounded-full bg-gradient-to-br from-indigo-200/40 via-sky-200/30 to-cyan-200/35 blur-3xl" />
        <div className="absolute -bottom-40 left-[-140px] h-[480px] w-[480px] rounded-full bg-gradient-to-br from-emerald-200/35 via-lime-200/20 to-cyan-200/25 blur-3xl" />
      </div>

      <div className="relative h-full w-full flex flex-col">
        <header className="flex h-16 items-center justify-between mx-auto w-full max-w-[1280px] px-6 md:px-8">
          <div className="flex items-center gap-3">
            <div className="relative grid h-9 w-9 place-items-center">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-cyan-400/40 to-blue-500/30 blur-[1px]" />
              <div className="relative grid h-8 w-8 place-items-center rounded-xl border border-slate-900/10 bg-white">
                <Wand2 className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight">Strategy Studio</div>
            </div>
          </div>
          <div className="hidden items-center gap-2 text-xs text-slate-500 md:flex">
            <span className="inline-flex items-center gap-1">
              <Rocket className="h-3.5 w-3.5 text-blue-500" />
              Build a quant strategy in seconds
            </span>
          </div>
        </header>

        <main className="flex h-[calc(100%-64px)] flex-col px-4 md:px-6 pb-6" style={{overflow:"hidden"}}>
          <div className="flex-1 min-h-0">
            <div className="flex h-full gap-6 md:gap-8">
              <VideoCarousel
                  title="Traditional Quantitative Analysis"
                  slides={STRATEGIES_SLIDES}
                  divider
              />
              <div className="hidden md:flex flex-col justify-center min-h-0" style={{ flex: '3.5 1 0', minWidth: 0 }}>
                <div
                  className="rounded-2xl border border-slate-900/10 bg-white/70 shadow-sm backdrop-blur-sm p-6 md:p-7"
                >
                  <div className="text-[13px] font-semibold text-slate-600 flex items-center gap-2">
                    Describe what you want. We'll do the rest.
                  </div>

                  <div className="mt-6 space-y-5">
                    <div
                      className={
                        "flex items-start gap-3 justify-end transition-all duration-500 " +
                        (stage >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3")
                      }
                    >
                      <div className="bg-blue-600 text-white px-4 py-3 rounded-2xl rounded-tr-sm shadow-lg max-w-[80%]">
                        <div className="text-[15px] leading-relaxed">{USER_PROMPT}</div>
                      </div>
                      <div className="w-9 h-9 rounded-full bg-white border border-slate-200 grid place-items-center shrink-0 shadow-sm">
                        <User className="h-4 w-4 text-slate-500" />
                      </div>
                    </div>

                    <div
                      className={
                        "flex items-start gap-3 transition-all duration-500 " +
                        (stage >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3")
                      }
                    >
                      <div className="w-9 h-9 rounded-full bg-emerald-600 grid place-items-center shrink-0 shadow-lg shadow-emerald-900/20">
                        <Bot className="h-5 w-5 text-white" />
                      </div>
                      <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm w-full">
                        <div className="text-[14px] text-slate-700 leading-relaxed">
                          {typedText}
                          {typedText.length < AI_REPLY.length ? (
                            <span className="ml-1 inline-block h-4 w-2 align-middle bg-emerald-500/80 animate-pulse" />
                          ) : null}
                        </div>
                        <div
                          className={
                            "mt-3 flex items-center gap-2 text-[12px] text-slate-500 transition-all duration-500 " +
                            (stage >= 3 ? "opacity-100" : "opacity-0")
                          }
                        >
                          <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                          Tool: preference questionnaire
                        </div>
                        <div
                          className={
                            "mt-2 flex items-center gap-2 text-[12px] text-slate-500 transition-all duration-500 " +
                            (stage >= 4 ? "opacity-100" : "opacity-0")
                          }
                        >
                          <GitBranch className="h-3.5 w-3.5 text-indigo-500" />
                          Tool: generate workflow graph
                        </div>
                        <div
                          className={
                            "mt-2 flex items-center gap-2 text-[12px] text-slate-500 transition-all duration-500 " +
                            (panel === "backtest" ? "opacity-100" : "opacity-0")
                          }
                        >
                          <BarChart3 className="h-3.5 w-3.5 text-emerald-600" />
                          Tool: run backtest
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-center min-h-0" style={{ flex: '3.5 1 0', minWidth: 0 }}>
                <div className="rounded-2xl border border-slate-900/10 bg-white/75 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between gap-4 border-b border-slate-900/10 px-6 py-4">
                    <div className="text-sm font-semibold tracking-tight text-slate-900">
                      {panel === "prefs"
                        ? "Auto-collect preferences"
                        : panel === "flow"
                        ? "Auto-build flow graph"
                        : "Auto-run backtest"}
                    </div>
                    <div className="text-xs font-medium text-slate-500">
                      {panel === "prefs" ? "5 quick questions" : panel === "flow" ? "step-by-step graph" : "instant preview"}
                    </div>
                  </div>

                  <div className="p-4 md:p-6">
                    <div className="relative h-[420px] md:h-[520px]">
                      <div
                        className={
                          "absolute inset-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] " +
                          (panel === "prefs" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none")
                        }
                      >
                        <div className="h-full rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-5">
                          <div className="text-sm font-semibold text-slate-800">Strategy preferences</div>
                          <div className="mt-4 grid gap-3 sm:grid-cols-2">
                            {[
                              { label: "Market", hint: "US stocks / Crypto / Futures" },
                              { label: "Risk", hint: "Max drawdown" },
                              { label: "Return", hint: "Annualized target" },
                              { label: "Interpret", hint: "White / gray / black box" },
                              { label: "Constraints", hint: "Leverage / capital / latency" },
                            ].map((s, idx) => {
                              const done = prefsStep >= idx + 1;
                              const active = prefsStep === idx + 1;
                              return (
                                <div
                                  key={s.label}
                                  className={
                                    "rounded-xl border p-4 transition-all duration-500 " +
                                    (done
                                      ? "border-emerald-200 bg-emerald-50/40"
                                      : active
                                      ? "border-blue-200 bg-blue-50/40"
                                      : "border-slate-200 bg-white")
                                  }
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-semibold text-slate-800">{s.label}</div>
                                    {done ? (
                                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                    ) : (
                                      <div
                                        className={
                                          "h-2.5 w-2.5 rounded-full " +
                                          (active ? "bg-blue-500 animate-pulse" : "bg-slate-300")
                                        }
                                      />
                                    )}
                                  </div>
                                  <div className="mt-1 text-xs text-slate-500">{s.hint}</div>
                                </div>
                              );
                            })}
                          </div>

                          <div
                            className={
                              "mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2 transition-all duration-500 " +
                              (prefsStep >= 5 ? "opacity-100" : "opacity-0")
                            }
                          >
                            <CheckCircle2 className="h-4 w-4" /> Preferences saved. Generating workflow…
                          </div>
                        </div>
                      </div>

                      <div
                        className={
                          "absolute inset-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] " +
                          (panel === "flow" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none")
                        }
                      >
                        <div className="h-full overflow-hidden rounded-xl border border-slate-200 bg-white">
                          <FlowCanvas currentFlowStep={flowStep} />
                        </div>
                      </div>

                      <div
                        className={
                          "absolute inset-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] " +
                          (panel === "backtest" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none")
                        }
                      >
                        <div className="h-full rounded-xl border border-slate-200 bg-white overflow-hidden">
                          <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">Backtest preview</div>
                            </div>
                            <div
                              className={
                                "text-xs font-semibold rounded-full px-3 py-1 border transition-all " +
                                (backtestState === "running"
                                  ? "border-amber-200 bg-amber-50 text-amber-700"
                                  : backtestState === "done"
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                  : "border-slate-200 bg-slate-50 text-slate-500")
                              }
                            >
                              {backtestState === "running"
                                ? "Running…"
                                : backtestState === "done"
                                ? "Done"
                                : "Queued"}
                            </div>
                          </div>

                          <div className="p-5">
                            <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-4">
                              <svg viewBox="0 0 520 180" className="w-full h-[180px]">
                                <defs>
                                  <linearGradient id="pnlFill" x1="0" x2="0" y1="0" y2="1">
                                    <stop offset="0" stopColor="rgba(59,130,246,0.22)" />
                                    <stop offset="1" stopColor="rgba(59,130,246,0)" />
                                  </linearGradient>
                                </defs>
                                <path
                                  d={`${backtestPath} L 496 168 L 24 168 Z`}
                                  fill="url(#pnlFill)"
                                  opacity={backtestState === "done" ? 1 : 0}
                                  style={{ transition: "opacity 400ms ease" }}
                                />
                                <path
                                  d={backtestPath}
                                  fill="none"
                                  stroke="#3b82f6"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  style={
                                    backtestState === "done"
                                      ? {
                                          strokeDasharray: 1400,
                                          strokeDashoffset: 0,
                                          transition: "stroke-dashoffset 900ms ease-out",
                                        }
                                      : {
                                          strokeDasharray: 1400,
                                          strokeDashoffset: 1400,
                                          transition: "stroke-dashoffset 900ms ease-out",
                                        }
                                  }
                                />
                              </svg>
                            </div>

                            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                              {[
                                { k: "Total Return", v: "+51.5%" },
                                { k: "Max Drawdown", v: "-9.2%" },
                                { k: "Sharpe", v: "1.34" },
                              ].map((m) => (
                                <div
                                  key={m.k}
                                  className="rounded-xl border border-slate-200 bg-white px-3 py-3"
                                >
                                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                    {m.k}
                                  </div>
                                  <div className="mt-1 text-sm font-bold text-slate-900">{m.v}</div>
                                </div>
                              ))}
                            </div>

                            <div
                              className={
                                "mt-4 text-xs text-slate-500 transition-opacity duration-500 " +
                                (backtestState === "done" ? "opacity-100" : "opacity-0")
                              }
                            >
                              Open Strategy Studio to edit nodes, rerun scenarios, and export.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress bar + controls — fixed bottom-right */}
          <div className="absolute bottom-6 right-6 z-50">
            <StepProgressBar
              currentStep={currentStep}
              stepProgress={stepProgress}
              isPaused={isPaused}
              onStepClick={handleStepClick}
              onPause={handlePause}
              onReplay={handleReplay}
            />
          </div>

          <div className="mt-4 w-full max-w-[760px] mx-auto shrink-0">
            <ChatBar
              Icon={ArrowUpRight}
              value={query}
              onChange={setQuery}
              placeholder="Describe the strategy you want to build…"
              onSend={(text) => {
                const t = (text ?? "").trim();
                if (!t) return;
                setQuery("");
                navigate("/strategies", { state: { prompt: t } });
              }}
            />
          </div>
        </main>
      </div>
    </div>
  );
}
